import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * GameStateContext - Game Lifecycle Management
 * Coordinates pause state, overlays, and game events
 * Completely separate from DinoGame.tsx internals
 */

export interface GameStateContextType {
  isPaused: boolean;
  setPaused: (paused: boolean) => void;
  showTutorial: boolean;
  setShowTutorial: (show: boolean) => void;
  showSettings: boolean;
  setShowSettings: (show: boolean) => void;
  showAchievementToast: boolean;
  setShowAchievementToast: (show: boolean) => void;
  currentAchievement: string | null;
  setCurrentAchievement: (achievement: string | null) => void;
  lastScore: number;
  setLastScore: (score: number) => void;
  isGameOver: boolean;
  setIsGameOver: (gameOver: boolean) => void;
}

const GameStateContext = createContext<GameStateContextType | undefined>(undefined);

export const GameStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPaused, setIsPausedState] = useState(false);
  const [showTutorial, setShowTutorialState] = useState(false);
  const [showSettings, setShowSettingsState] = useState(false);
  const [showAchievementToast, setShowAchievementToastState] = useState(false);
  const [currentAchievement, setCurrentAchievementState] = useState<string | null>(null);
  const [lastScore, setLastScoreState] = useState(0);
  const [isGameOver, setIsGameOverState] = useState(false);

  const setPaused = useCallback((paused: boolean) => {
    setIsPausedState(paused);
  }, []);

  const setShowTutorial = useCallback((show: boolean) => {
    setShowTutorialState(show);
  }, []);

  const setShowSettings = useCallback((show: boolean) => {
    setShowSettingsState(show);
  }, []);

  const setShowAchievementToast = useCallback((show: boolean) => {
    setShowAchievementToastState(show);
  }, []);

  const setCurrentAchievement = useCallback((achievement: string | null) => {
    setCurrentAchievementState(achievement);
  }, []);

  const setLastScore = useCallback((score: number) => {
    setLastScoreState(score);
  }, []);

  const setIsGameOver = useCallback((gameOver: boolean) => {
    setIsGameOverState(gameOver);
  }, []);

  const value: GameStateContextType = {
    isPaused,
    setPaused,
    showTutorial,
    setShowTutorial,
    showSettings,
    setShowSettings,
    showAchievementToast,
    setShowAchievementToast,
    currentAchievement,
    setCurrentAchievement,
    lastScore,
    setLastScore,
    isGameOver,
    setIsGameOver,
  };

  return (
    <GameStateContext.Provider value={value}>
      {children}
    </GameStateContext.Provider>
  );
};

export const useGameState = (): GameStateContextType => {
  const context = useContext(GameStateContext);
  if (!context) {
    throw new Error('useGameState must be used within GameStateProvider');
  }
  return context;
};
